// ═══════════════════════════════════════════════════════════════════════════════
//  src/interview/interviewApi.js
//  INTERVIEW ROOM — capa de datos del microservicio
//
//  Habla únicamente con /api/v3/interview. No importa nada del tutor, del
//  assessment ni del Dojo: si este módulo desaparece, no se rompe nada más.
//
//  ── POR QUÉ HTTP Y NO WEBSOCKETS ────────────────────────────────────────────
//  Overmind usa WebSockets porque es una app de escritorio con captura de audio
//  continua contra Deepgram y una cola de tareas compartida entre modos. NSL no
//  tiene nada de eso: el reconocimiento de voz corre en el navegador (gratis y
//  a 0 ms), y cada paso de la entrevista es una petición con su respuesta.
//
//  Un socket aquí costaría estado de conexión, reconexión, sesiones pegajosas
//  en Render y quedaría fuera del rate limit por petición y del Token Guardian
//  — los dos escudos que ya protegen el resto de la plataforma. Cinco
//  endpoints HTTP hacen el mismo trabajo y heredan la seguridad existente.
// ═══════════════════════════════════════════════════════════════════════════════

const BASE = `${import.meta.env.VITE_API_URL || ''}/api/v3/interview`;

function authHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function request(path, options = {}) {
  const res = await fetch(`${BASE}${path}`, { headers: authHeaders(), ...options });

  if (!res.ok) {
    let detail = 'No pudimos conectar con la sala de entrevistas.';
    try {
      const body = await res.json();
      if (typeof body?.detail === 'string') detail = body.detail;
      else if (body?.detail?.message) detail = body.detail.message;
    } catch { /* respuesta sin JSON */ }
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }

  return res.json();
}

const post = (path, body) => request(path, { method: 'POST', body: JSON.stringify(body) });

/** Perfiles prefabricados, fases y reparto de preguntas. Sin costo. */
export const getPresets = () => request('/presets');

/**
 * Abre una entrevista.
 * @param {{mode:'assisted'|'free', source:'preset'|'real', language:'en'|'es',
 *          questionCount:number, presetId?:string, roleLabel?:string,
 *          cv?:string, vacancy?:string}} cfg
 */
export const startInterview = (cfg) => post('/start', {
  mode: cfg.mode,
  source: cfg.source,
  language: cfg.language,
  question_count: cfg.questionCount,
  ...(cfg.presetId ? { preset_id: cfg.presetId } : {}),
  ...(cfg.roleLabel ? { role_label: cfg.roleLabel } : {}),
  ...(cfg.cv ? { cv: cfg.cv } : {}),
  ...(cfg.vacancy ? { vacancy: cfg.vacancy } : {}),
});

/** La siguiente pregunta, en la fase que toca. */
export const nextQuestion = (sessionId) => post('/question', { session_id: sessionId });

/** Modo asistido: la respuesta modelo que el alumno va a leer. */
export const getCoachAnswer = (sessionId, turnId) =>
  post('/coach', { session_id: sessionId, turn_id: turnId });

/** Envía lo que dijo el alumno y recibe la calificación. */
export const submitAnswer = ({ sessionId, turnId, transcript, seconds }) =>
  post('/answer', {
    session_id: sessionId,
    turn_id: turnId,
    transcript,
    duration_seconds: Math.max(0, Math.min(900, seconds || 0)),
  });

/** Cierra la entrevista y devuelve el reporte consolidado. */
export const finishInterview = (sessionId) => post('/finish', { session_id: sessionId });

/** Reporte de una entrevista ya cerrada. */
export const getReport = (sessionId) => request(`/report/${sessionId}`);
